/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState, useRef } from 'react';
import {
    object, func, number, string, bool,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
    Grid, Card, IconButton, Typography, Button, TextField, FormControl, InputLabel,
} from '@material-ui/core';
import ReactHtmlParser from 'react-html-parser';
import ResponsiveImage from '../../../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';

// Number of ms until error is dismissed
const errorTimeoutTime = 2000;
// Number of ms that error Transition lasts
const transitionTime = 500;

const useStyles = makeStyles(({ palette, spacing, breakpoints }) => ({
    itemContainer: {
        boxShadow: `1px 1px 3px -1px ${palette?.black}`,
        position: 'relative',
        width: '395px',
        maxWidth: '395px',
        height: '105px',
        display: 'block',
        margin: '12px',
        [breakpoints.down('lg')]: {
            margin: '12px',
        },
        [breakpoints.down('md')]: {
            maxWidth: '355px',
        },
        [breakpoints.down('xs')]: {
            margin: '12px auto',
            width: '98%',
        },
    },
    itemInnerContainer: ({ disabled }) => ({
        opacity: disabled ? 0.7 : 1,
        display: 'flex',
        flexFlow: 'row nowrap',
        lineBreak: 'anywhere',
    }),
    errorContainer: ({ hasError, hideError }) => ({
        position: 'absolute',
        background: palette?.background?.paper || '#fff',
        color: palette?.error.light || 'rgb(220, 30, 30)',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        display: 'flex',
        placeItems: 'center',
        justifyContent: 'center',
        zIndex: hideError ? 0 : 2,
        opacity: hasError ? 1 : 0,
        transition: `opacity ${transitionTime / 1000}s`,
    }),
    imageContainer: {
        position: 'relative',
        padding: '0',
        height: '105px',
        width: '105px',
        maxWidth: '105px',
    },
    imageOverlay: {
        position: 'absolute',
        top: '0',
        left: '0',
        height: '100%',
        width: '100%',
        backgroundColor: palette?.bgTextOverlay1 || 'rgba(51, 51, 51, 0.8)',
        color: palette?.getContrastText(palette?.bgTextOverlay1) || '#fff',
        fontWeight: 'bold',
        fontSize: '16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

    },
    mainContent: {
        paddingLeft: spacing(2),
        boxSizing: 'border-box',
    },
    buttonContainer: {
        display: 'flex',
        flexFlow: 'row nowrap',
        justifyContent: 'space-between',
    },
    addRemoveContainer: {
        alignItems: 'center',
        maxWidth: 'fit-content',
        flexFlow: 'row nowrap',
        display: 'flex',
        boxSizing: 'border-box',
        paddingRight: '5px',
        [breakpoints.down('xs')]: {
            paddingRight: '5%',
        },
    },
    removeButtonContainer: {
        width: '150px',
        [breakpoints.down('md')]: {
            marginRight: '1%',
            width: '50%',
        },
        '@media screen and (max-width: 340px)': {
            width: '78px',
            height: '2.5em',
        },
    },
    removeButton: {
        borderRadius: '20px',
        display: 'flex',
        width: '107px',
        maxWidth: '107px',
        height: '40px',
        [breakpoints.down('md')]: {
            width: '95%',
        },
        '@media screen and (max-width: 340px)': {
            fontSize: '14px',
        },
    },
    itemImage: {
        height: '105px',
        width: '105px',
        marginBottom: '-6px',
    },
    itemName: {
        paddingRight: spacing(3),
        fontSize: '16px',
        lineHeight: '20px',
    },
    inputField: {
        padding: '0px 8px',
        '& input': {
            padding: '0',
            textAlign: 'center',
            width: '2.5em',
            height: '42px',
        },
        '@media screen and (max-width: 340px)': {
            padding: '0px 4px',
            '& input': {
                width: '2em',
            },
        },
    },
    itemNumber: {
        fontSize: '18px',
        minWidth: '25px',
    },
    itemButton: {
        padding: '0px',
        display: 'block',
        margin: 'auto',
        width: '32px',
    },
    itemButtonSVG: {
        height: '40px',
        width: '40px',
        '&:hover': {
            cursor: 'pointer',
            '& > *': {
                fill: palette.colorPrimary,
                stroke: palette.white,
            },
        },
        '& > *': {
            transition: 'all 0.25s',
        },
    },
    rootInputLabel: {
        display: 'none',
    },
}));

const CYOItem = ({
    itemData,
    handleItemSelection,
    numberSelected,
    error,
    clearError,
    handleDisabledInteraction,
    removeItem,
    disabled,
}) => {
    const [hideError, setHideError] = useState(true);
    const classes = useStyles({ hasError: !!error, hideError, disabled });
    const errorTimeout = useRef();
    const transitionTimeout = useRef();

    const handleRemoveItem = (e) => {
        e.stopPropagation();
        e.preventDefault();
        removeItem(itemData.legacyId);
    };

    const handleIncrement = (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleItemSelection(1);
    };

    const handleDecrement = (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleItemSelection(-1);
    };

    const handleInteraction = () => {
        if (disabled) {
            handleDisabledInteraction(itemData.legacyId);
        }
    };

    // Clears error after timeout time
    useEffect(() => {
        if (error) {
            setHideError(false);
            if (errorTimeout.current) {
                clearTimeout(errorTimeout.current);
                errorTimeout.current = null;
            }
            errorTimeout.current = setTimeout(() => {
                clearError(itemData.legacyId);
            }, errorTimeoutTime);
        } else {
            if (transitionTimeout.current) {
                clearTimeout(transitionTimeout);
                transitionTimeout.current = null;
            }
            transitionTimeout.current = setTimeout(() => {
                setHideError(true);
            }, transitionTime);
        }
    }, [error]);

    // regex will replace every alphabetic or numberic character before ":" with empty string ''
    // thus replacing http or https from image url
    const itemImagePath = itemData.image?.path?.replace(/(^\w+:|^)/, '');
    const itemImageName = itemData.image?.name;
    const imagePath = `${itemImagePath}${itemImageName}x.jpg`;

    return (
        <Grid
            component={Card}
            className={classes.itemContainer}
            item
            spacing={2}
            onClick={handleInteraction}
            data-testid="cyoItemContainer"
        >
            <Typography className={`${classes.errorContainer}`}>
                {error}
            </Typography>
            <div className={classes.itemInnerContainer}>
                <div className={classes.imageContainer}>
                    {numberSelected ? (
                        <Typography className={classes.imageOverlay}>
                            ADDED
                        </Typography>
                    ) : null}
                    <ResponsiveImage className={classes.itemImage} path={imagePath} alt={itemData.name} params={{ desktop: 'quality=100', tablet: 'quality=100',  mobile: 'quality=100' }} />
                </div>
                <Grid spacing={0} className={classes.mainContent} alignItems="center" justify="space-between" container>
                    <Grid item xs={12}>
                        <Typography className={classes.itemName} variant="body2">
                            {ReactHtmlParser(itemData.name)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} className={classes.buttonContainer}>
                        <div className={classes.removeButtonContainer}>
                            {numberSelected ? (
                                <Button
                                    data-testid="cyoRemoveButton"
                                    variant="outlined"
                                    color="primary"
                                    className={classes.removeButton}
                                    onClick={handleRemoveItem}
                                >
                                    REMOVE
                                </Button>
                            ) : null}
                        </div>
                        <div className={classes.addRemoveContainer}>
                            <IconButton
                                data-testid="cyoDecrementButton"
                                disabled={disabled}
                                className={classes.itemButton}
                                onClick={handleDecrement}
                                aria-label="Add"

                            >
                                <svg className={classes.itemButtonSVG} width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="20" cy="20" r="19.5" stroke="black" />
                                    <path d="M9 19.5H31" stroke="black" strokeWidth="2" />
                                </svg>
                            </IconButton>
                            <FormControl
                                classes={{
                                    root: classes.rootFormControl,
                                }}
                            >
                                <InputLabel
                                    classes={{
                                        root: classes.rootInputLabel,
                                    }}
                                    htmlFor={ReactHtmlParser(itemData.name)}
                                >
                                    Add quantity
                                </InputLabel>
                                <TextField
                                    variant="outlined"
                                    id={ReactHtmlParser(itemData.name)}
                                    onKeyDown={(e) => {
                                        // e.preventDefault();
                                        if (e.keyCode === 38) {
                                            handleItemSelection(1);
                                        } else if (e.keyCode === 40) {
                                            handleItemSelection(-1);
                                        }
                                    }}
                                    inputProps={{
                                        'data-testid': 'cyo-item-input',
                                    }}
                                    value={numberSelected}
                                    classes={{ root: classes.inputField }}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const diff = (parseInt(e.target.value, 10) || 0) - numberSelected;
                                        handleItemSelection(diff);
                                    }}
                                />
                            </FormControl>
                            <IconButton
                                data-testid="cyoIncrementButton"
                                disabled={disabled}
                                className={classes.itemButton}
                                onClick={handleIncrement}
                                aria-label="Close"
                            >
                                <svg className={classes.itemButtonSVG} width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="20" cy="20" r="19.5" stroke="black" />
                                    <path d="M20 10V30M10 19.5H30" stroke="black" strokeWidth="2" />
                                </svg>
                            </IconButton>
                        </div>
                    </Grid>
                </Grid>
            </div>
        </Grid>
    );
};

CYOItem.propTypes = {
    itemData: object.isRequired,
    handleItemSelection: func.isRequired,
    numberSelected: number.isRequired,
    removeItem: func.isRequired,
    handleDisabledInteraction: func,
    disabled: bool,
    error: string,
    clearError: func,
};

CYOItem.defaultProps = {
    disabled: false,
    error: null,
    clearError: () => {},
    handleDisabledInteraction: () => {},
};

export default CYOItem;
